import { useState, useCallback, useRef, useEffect } from 'react'
import { Shard } from '../types/shard'
import type { HistoryEvent } from '../components/ShardInspectorModal'

interface UseShardsOptions {
  onMessage?: (data: any) => void
  onShardBlocked?: (shard: Shard) => void
  onAllShardsDone?: () => void
}

/**
 * Detect changes between old and new shard and generate history events
 */
function detectChanges(oldShard: Shard, newShard: Shard): HistoryEvent[] {
  const events: HistoryEvent[] = []
  const timestamp = new Date().toISOString()

  // Status change
  if (oldShard.status !== newShard.status) {
    events.push({
      timestamp,
      event: 'Status Changed',
      details: `Status changed: ${oldShard.status} → ${newShard.status}`,
    })
  }

  // Assignment change
  const oldAssignee = oldShard.assigned_worker || oldShard.assignee
  const newAssignee = newShard.assigned_worker || newShard.assignee
  if (oldAssignee !== newAssignee) {
    if (!oldAssignee && newAssignee) {
      events.push({
        timestamp,
        event: 'Assigned',
        details: `Assigned to ${newAssignee}`,
      })
    } else if (oldAssignee && !newAssignee) {
      events.push({
        timestamp,
        event: 'Unassigned',
        details: `Unassigned from ${oldAssignee}`,
      })
    } else {
      events.push({
        timestamp,
        event: 'Reassigned',
        details: `Reassigned from ${oldAssignee} to ${newAssignee}`,
      })
    }
  }

  // Title change
  if (oldShard.title !== newShard.title) {
    events.push({
      timestamp,
      event: 'Title Updated',
      details: `Title changed to "${newShard.title}"`,
    })
  }

  // Priority change
  if (oldShard.priority !== newShard.priority && newShard.priority) {
    events.push({
      timestamp,
      event: 'Priority Changed',
      details: oldShard.priority
        ? `Priority changed: ${oldShard.priority} → ${newShard.priority}`
        : `Priority set to ${newShard.priority}`,
    })
  }

  // Type change
  if (oldShard.type !== newShard.type && newShard.type) {
    events.push({
      timestamp,
      event: 'Type Changed',
      details: oldShard.type
        ? `Type changed: ${oldShard.type} → ${newShard.type}`
        : `Type set to ${newShard.type}`,
    })
  }

  return events
}

/**
 * Create the initial "Created" history event for a new shard
 */
function createInitialHistory(shard: Shard): HistoryEvent[] {
  const timestamp = shard.created_at || new Date().toISOString()
  const events: HistoryEvent[] = [
    {
      timestamp,
      event: 'Created',
      details: `Shard created with status: ${shard.status}`,
    },
  ]

  // Add assignment event if already assigned when first seen
  const assignee = shard.assigned_worker || shard.assignee
  if (assignee) {
    events.push({
      timestamp,
      event: 'Assigned',
      details: `Assigned to ${assignee}`,
    })
  }

  return events
}

/**
 * Check if all shards are in DONE status
 */
function areAllShardsDone(shards: Shard[]): boolean {
  return shards.length > 0 && shards.every((shard) => shard.status === 'DONE')
}

export function useShards(options: UseShardsOptions = {}) {
  const [shards, setShards] = useState<Shard[]>([])
  // Use ref for history map to avoid unnecessary re-renders
  // The map is keyed by shard ID
  const historyMapRef = useRef<Map<string, HistoryEvent[]>>(new Map())
  // State version to trigger re-renders when history changes
  const [, setHistoryVersion] = useState(0)
  // Track whether we've already triggered the all-done callback
  const allDoneTriggeredRef = useRef(false)

  const getShardHistory = useCallback((shardId: string): HistoryEvent[] => {
    return historyMapRef.current.get(shardId) || []
  }, [])

  // Detect when all shards transition to DONE status
  useEffect(() => {
    const allDone = areAllShardsDone(shards)

    if (allDone && !allDoneTriggeredRef.current) {
      // Transitioning TO all-done state
      allDoneTriggeredRef.current = true
      options.onAllShardsDone?.()
    } else if (!allDone && allDoneTriggeredRef.current) {
      // No longer all-done (e.g., new shard added or shard status changed)
      allDoneTriggeredRef.current = false
    }
  }, [shards, options.onAllShardsDone])

  const handleWebSocketMessage = useCallback((data: any) => {
    // Call custom handler if provided
    options.onMessage?.(data)

    // Handle shard-related messages
    if (data.type === 'shard:update') {
      setShards((prev) => {
        const shard = data.shard as Shard
        const index = prev.findIndex((s) => s.id === shard.id)

        if (index >= 0) {
          // Update existing shard - detect changes first
          const oldShard = prev[index]!
          const changeEvents = detectChanges(oldShard, shard)

          // Detect transition to BLOCKED status
          if (oldShard.status !== 'BLOCKED' && shard.status === 'BLOCKED') {
            options.onShardBlocked?.(shard)
          }

          if (changeEvents.length > 0) {
            const existingHistory = historyMapRef.current.get(shard.id) || []
            historyMapRef.current.set(shard.id, [...existingHistory, ...changeEvents])
            setHistoryVersion(v => v + 1)
          }

          const updated = [...prev]
          updated[index] = { ...updated[index], ...shard }
          return updated
        } else {
          // Add new shard - create initial history
          const initialHistory = createInitialHistory(shard)
          historyMapRef.current.set(shard.id, initialHistory)
          setHistoryVersion(v => v + 1)

          return [...prev, shard]
        }
      })
    } else if (data.type === 'shard:moved') {
      setShards((prev) => {
        const index = prev.findIndex((s) => s.id === data.shardId)
        if (index >= 0) {
          const oldShard = prev[index]!
          const updated = [...prev]
          const newStatus = data.newStatus as Shard['status']

          // Detect transition to BLOCKED status
          if (oldShard.status !== 'BLOCKED' && newStatus === 'BLOCKED') {
            const blockedShard = { ...oldShard, status: newStatus }
            options.onShardBlocked?.(blockedShard)
          }

          // Add status change to history
          if (oldShard.status !== newStatus) {
            const existingHistory = historyMapRef.current.get(data.shardId) || []
            historyMapRef.current.set(data.shardId, [
              ...existingHistory,
              {
                timestamp: new Date().toISOString(),
                event: 'Status Changed',
                details: `Status changed: ${oldShard.status} → ${newStatus}`,
              },
            ])
            setHistoryVersion(v => v + 1)
          }

          updated[index] = {
            ...oldShard,
            status: newStatus,
          } as Shard
          return updated
        }
        return prev
      })
    } else if (data.type === 'shards:list') {
      // Initial load or bulk update
      const shardsList = data.shards || []

      // Create initial history for any shards we haven't seen before
      shardsList.forEach((shard: Shard) => {
        if (!historyMapRef.current.has(shard.id)) {
          historyMapRef.current.set(shard.id, createInitialHistory(shard))
        }
      })
      setHistoryVersion(v => v + 1)

      setShards(shardsList)
    }
  }, [options])

  return {
    shards,
    handleWebSocketMessage,
    getShardHistory,
  }
}
